import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "leaflet/dist/leaflet.css";
import { Button, message, Table, Spin, Checkbox, Input } from "antd";
import "./CubaMap.css";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";

export const Axios = axios.create({
  baseURL: process.env.REACT_APP_APPI_ADDRESS,
});

const CubaMap = ({ center, onCoordinatesSelect, data, onClose }) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const markerRef = useRef(null); // Usamos markerRef para almacenar el marcador
  const [point, setPoint] = useState(null);
  const [originalPoint, setOriginalPoint] = useState(null);
  const [originalReferencePoint, setOriginalReferencePoint] = useState(Number(data.referencepoint) || 0);
  const [originalObservations, setOriginalObservations] = useState(data.observations ?? "");
  const [hasChanges, setHasChanges] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [spinTitle, setSpinTitle] = useState(t("global.loading"));
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [messageApi] = message.useMessage();
  const { TextArea } = Input;
  const [referencePoint, setReferencePoint] = useState(
    Number(data.referencepoint) || 0
  );
  const [observations, setObservations] = useState(data.observations ?? "");

  console.log(data);

  // Función para verificar si hay cambios en el formulario y si es válido
  const checkForChanges = useCallback(() => {
    // Verificar cambios en coordenadas
    let coordsChanged = false;
    if (originalPoint && point) {
      // Ambos existen, comparar valores
      coordsChanged = originalPoint.lat !== point.lat || originalPoint.lng !== point.lng;
    } else if (originalPoint !== point) {
      // Uno es null y el otro no (cambio de estado)
      coordsChanged = true;
    }

    const referenceChanged = originalReferencePoint !== referencePoint;
    const observationsChanged = originalObservations !== observations;

    const hasAnyChanges = coordsChanged || referenceChanged || observationsChanged;

    // Validar que si referencePoint está marcado, observations no esté vacío
    const isValidForm = referencePoint !== 1 || (observations && observations.trim() !== "");

    setHasChanges(hasAnyChanges && isValidForm);
  }, [originalPoint, point, originalReferencePoint, referencePoint, originalObservations, observations]);

  // Ejecutar verificación cuando cambien los valores
  useEffect(() => {
    checkForChanges();
  }, [point, referencePoint, observations, checkForChanges]);

  const headers = useMemo(
    () => ({
      accept: "*/*",
      Authorization: "Bearer " + sessionStorage.getItem("token"),
      "Content-Type": "application/json",
    }),
    []
  );

  const loadMap = useCallback(() => {
    const L = require("leaflet");

    if (mapRef.current === null) {
      const mapInstance = L.map("map", {
        center: center || [21.5, -77.0],
        zoom: 15, // Zoom inicial configurado
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
      }).addTo(mapInstance);

      mapRef.current = mapInstance;
      setIsMapLoading(false);

      if (data.latitude && data.longitude) {
        const markerIcon = L.icon({
          iconUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
          shadowUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          shadowSize: [41, 41],
        });

        // Agregar marcador inicial en las coordenadas dadas por data
        markerRef.current = L.marker([data.latitude, data.longitude], {
          icon: markerIcon,
        }).addTo(mapInstance);
        const initialPoint = { lat: data.latitude, lng: data.longitude };
        setPoint(initialPoint);
        setOriginalPoint(initialPoint);
        setHasChanges(false);

        // Centrar el mapa en las coordenadas usando flyTo
        mapInstance.flyTo([data.latitude, data.longitude], 15, {
          animate: true,
          duration: 1.5,
        });
      } else {
        // Si no hay coordenadas iniciales, permitir nueva selección
        setOriginalPoint(null);
        setHasChanges(false);
      }
    }
  }, []);

  useEffect(() => {
    loadMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [loadMap]);  // Ejecutar cuando loadMap cambie

  // Manejar coordenadas existentes cuando el mapa está listo
  useEffect(() => {
    if (mapRef.current && data.latitude && data.longitude && !isMapLoading) {
      const L = require("leaflet");
      const markerIcon = L.icon({
        iconUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        shadowSize: [41, 41],
      });

      // Eliminar marcador existente si existe
      if (markerRef.current) {
        markerRef.current.remove();
      }

      // Crear nuevo marcador
      markerRef.current = L.marker([data.latitude, data.longitude], {
        icon: markerIcon,
      }).addTo(mapRef.current);
      const initialPoint = { lat: data.latitude, lng: data.longitude };
      setPoint(initialPoint);
      setOriginalPoint(initialPoint);
      setHasChanges(false);

      // Centrar el mapa en las coordenadas
      mapRef.current.flyTo([data.latitude, data.longitude], 15, {
        animate: true,
        duration: 1.5,
      });
    }
  }, [data.latitude, data.longitude, isMapLoading]);

  useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.flyTo(center, 15, {
        animate: true,
        duration: 1.5,
      });
    }
  }, [center]);

  useEffect(() => {
    setReferencePoint(Number(data.referencepoint) || 0);
    setObservations(data.observations ?? "");
  }, [data]);

  // Manejar el evento de doble clic para seleccionar coordenadas
  useEffect(() => {
    if (mapRef.current && !isMapLoading) {
      const handleDoubleClick = (event) => {
        const { lat, lng } = event.latlng;
        const newPoint = {
          lat: parseFloat(lat.toFixed(6)),
          lng: parseFloat(lng.toFixed(6)),
        };

        const L = require("leaflet");
        const markerIcon = L.icon({
          iconUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
          shadowUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          shadowSize: [41, 41],
        });

        // Eliminar el marcador existente si existe
        if (markerRef.current) {
          markerRef.current.remove();
        }

        // Crear nuevo marcador y asignarlo a markerRef
        markerRef.current = L.marker([newPoint.lat, newPoint.lng], {
          icon: markerIcon,
        }).addTo(mapRef.current);
        setPoint(newPoint); // Actualizar el estado del punto seleccionado
        onCoordinatesSelect && onCoordinatesSelect(newPoint); // Llamar a la función de callback con la nueva coordenada
      };

      mapRef.current.on("dblclick", handleDoubleClick);

      // Cleanup para remover el listener
      return () => {
        if (mapRef.current) {
          mapRef.current.off("dblclick", handleDoubleClick);
        }
      };
    }
  }, [onCoordinatesSelect, isMapLoading]);

  const handleSendCoordinates = useCallback(() => {
    // Validar que si el punto de referencia está marcado, las observaciones sean obligatorias
    if (referencePoint === 1 && (!observations || observations.trim() === "")) {
      messageApi.open({
        type: "warning",
        content: "Las observaciones son obligatorias cuando se marca 'Punto de referencia'",
        duration: 3,
      });
      return;
    }

    setSpinning(true);
    setSpinTitle(t("global.processing_data"));
    Axios.post(
      "sd/updateInvoiceCoordinates",
      {
        idinvoice: data.idinvoice,
        latitude: point?.lat,
        longitude: point?.lng,
        referencepoint: referencePoint,
        observations,
      },
      { headers }
    )
      .then((response) => {
        messageApi.open({
          type: response.data.success ? "success" : "error",
          content: response.data.message,
          duration: 2.5,
        });
        setSpinning(false);
        if (response.data.success && onClose) onClose();
      })
      .catch(() => {
        setSpinning(false);
        messageApi.open({
          type: "error",
          content: t("map.error_saving"),
          duration: 2.5,
        });
      });
  }, [
    data.idinvoice,
    headers,
    messageApi,
    onClose,
    point,
    t,
    referencePoint,
    observations,
  ]);

  const handleClearCoordinates = useCallback(() => {
    setPoint(null);
    if (markerRef.current) {
      markerRef.current.remove(); // Eliminar el marcador actual
      markerRef.current = null;
    }
  }, []);

  const columns = [
    { title: t("map.latitude"), dataIndex: "lat", key: "lat" },
    { title: t("map.longitude"), dataIndex: "lng", key: "lng" },
  ];

  return (
    <div className="cuba-map-container">
      <Spin spinning={spinning} tip={spinTitle}>
        <div className="cuba-map-layout">
          <div className="cuba-map-section">
            <div id="map" className="cuba-map">
              {isMapLoading && (
                <div className="map-loading-overlay">
                  <Spin tip={t("global.loading_map")} size="large" />
                </div>
              )}
            </div>
          </div>
          
          <div className="cuba-map-sidebar">
            <div className="sidebar-content">
              <div className="sidebar-header">
                <h3 className="coordinates-title">
                  {t("map.coordinates")}
                </h3>
                <p className="instruction-text">
                  {t("map.double_click_instruction") || "Doble click en el mapa para seleccionar coordenadas"}
                </p>
              </div>

              <div className="coordinates-section">
                <Table
                  dataSource={
                    point ? [{ key: 0, lat: point.lat, lng: point.lng }] : []
                  }
                  columns={columns}
                  pagination={false}
                  bordered
                  size="small"
                  className="coordinates-table"
                  locale={{
                    emptyText: t("map.no_coordinates") || "Sin coordenadas seleccionadas"
                  }}
                />
              </div>

              <div className="form-section">
                <Checkbox
                  className="reference-checkbox"
                  checked={referencePoint === 1}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setReferencePoint(isChecked ? 1 : 0);
                    // Si se desmarca el checkbox, limpiar las observaciones
                    if (!isChecked) {
                      setObservations("");
                    }
                  }}
                >
                  {t("map.reference_point")}
                </Checkbox>

                <TextArea
                  rows={3}
                  placeholder={
                    referencePoint === 1
                      ? `${t("map.observations")} (Obligatorio)`
                      : t("map.observations")
                  }
                  className="observations-textarea"
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  maxLength={500}
                  showCount
                  status={
                    referencePoint === 1 && (!observations || observations.trim() === "")
                      ? "error"
                      : undefined
                  }
                />
              </div>

              <div className="actions-section">
                <Button
                  icon={<CloseOutlined />}
                  type="default"
                  danger
                  onClick={handleClearCoordinates}
                  className="clear-btn"
                  size="large"
                >
                  {t("map.clear_coordinates")}
                </Button>
                <Button
                  icon={<SaveOutlined />}
                  type="primary"
                  onClick={handleSendCoordinates}
                  className="save-btn"
                  size="large"
                  disabled={!hasChanges}
                >
                  {t("map.save_coordinates")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default CubaMap;
